import { Microscope, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY, SERVICES } from "@/lib/site";

function WhatsappIcon({ className = "", ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.075-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M20.52 3.449C18.24 1.245 15.24 0 12.05 0 5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.52 3.449zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.511-5.26c.002-5.45 4.437-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.884 9.885z"/>
    </svg>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 bg-teal-600 text-white">
                <Microscope className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <span className="font-display text-lg font-semibold text-white">{COMPANY.shortName}</span>
            </div>
            <p className="mt-5 max-w-md text-slate-400 leading-relaxed">
              {COMPANY.tagline} — Xpertee Pharma Services partners with regulated pharma manufacturers across API
              procurement, GMP compliance, audits and laboratory testing.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-teal-400 font-medium">Services</div>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#services`}
                    data-testid={`footer-link-${s.id}`}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-teal-400 font-medium">Get in touch</div>
            <ul className="mt-5 space-y-4">
              <li className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 text-teal-400 shrink-0" strokeWidth={1.75} />
                <span className="text-slate-400">{COMPANY.address}</span>
              </li>
              <li>
                <a href={`tel:${COMPANY.phone.replace(/\s|\(|\)|-/g, "")}`} className="flex items-center gap-3 text-sm text-slate-400 hover:text-white transition-colors">
                  <Phone className="w-4 h-4 text-teal-400" strokeWidth={1.75} />
                  {COMPANY.phone}
                </a>
              </li>
              <li>
                <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\s|\+|\(|\)|-/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm text-slate-400 hover:text-white transition-colors">
                  <WhatsappIcon className="w-4 h-4 text-[#25D366]" />
                  WhatsApp {COMPANY.whatsapp}
                </a>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-sm text-slate-400 hover:text-white transition-colors">
                  <Mail className="w-4 h-4 text-teal-400" strokeWidth={1.75} />
                  {COMPANY.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            © {year} {COMPANY.legalName}. All rights reserved.
          </p>
          <p className="text-xs text-slate-500">
            Confidentiality respected · Global operations
          </p>
        </div>
      </div>
    </footer>
  );
}
